import { Alert } from "@omni/ui/components/Alert/alert.component";
import { isDefined } from "@omni/utils/helper.utils";
import { Suspense, useEffect } from "react";
import { useSelector } from "react-redux";

import { MollieProvider } from "@/components/Checkout/Payment/mollie.provider";
import { Spinner } from "@/components/General/Spinner/spinner.component";
import { useThunkDispatch } from "@/redux/dispatcher";
import * as PaymentMethodsModule from "@/redux/modules/api/data/checkout/paymentMethods/paymentMethods.module";
import * as PaymentMethodsSelectors from "@/redux/modules/api/data/checkout/paymentMethods/paymentMethods.selectors";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import * as CheckoutStepTracker from "@/services/tracking/trackers/tools/checkout/checkoutStep.tracker";
import { useUrlErrorMessage } from "@/utils/hooks/useUrlErrorMessage.hook";

import { useCheckout } from "../useCheckout.hook";
import { PaymentMethodsContainer } from "./paymentMethods/paymentMethods.container";

const PaymentContainer = () => {
    const { t } = useGranite();
    const dispatch = useThunkDispatch();

    const paymentMethods = useSelector(
        PaymentMethodsSelectors.getPaymentMethods
    );
    const isPaymentMethodsLoading = useSelector(
        PaymentMethodsSelectors.isLoading
    );
    const isPaymentMethodsLoadedSuccessfully = useSelector(
        PaymentMethodsSelectors.isSuccessfullyLoaded
    );
    const errorMessage = useSelector(PaymentMethodsSelectors.getError)?.message;
    const hasError = isDefined(errorMessage);

    const { cart, cartLoading, cartLoadedSuccessfully } = useCheckout();

    useUrlErrorMessage({
        id: "payment-error-message",
        status: "danger",
        dismissible: true
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        dispatch(PaymentMethodsModule.load());
    }, []);

    useEffect(() => {
        if (
            !cartLoadedSuccessfully ||
            !isPaymentMethodsLoadedSuccessfully ||
            !isDefined(cart)
        ) {
            return;
        }

        CheckoutStepTracker.trackPaymentStart(cart);
    }, [cart, cartLoadedSuccessfully, isPaymentMethodsLoadedSuccessfully]);

    const isLoading =
        isPaymentMethodsLoading ||
        !isPaymentMethodsLoadedSuccessfully ||
        (!cart && cartLoading);

    return (
        <Suspense fallback={<p>{t("common.loading")}</p>}>
            {!hasError && isLoading && (
                <div className={"text-center"}>
                    <Spinner />
                </div>
            )}
            {!hasError && !isLoading && !!paymentMethods && (
                <MollieProvider>
                    <PaymentMethodsContainer methods={paymentMethods} />
                </MollieProvider>
            )}
            {hasError && (
                <Alert
                    color={"danger"}
                    closeAriaLabel={t("general.alert.close")}
                >
                    {errorMessage}
                </Alert>
            )}
        </Suspense>
    );
};

export { PaymentContainer };
